import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Suppliers.css';

import priorityP1 from '../assets/priority/priority-p1.svg';
import priorityP2 from '../assets/priority/priority-p2.svg';
import priorityP3 from '../assets/priority/priority-p3.svg';
import priorityP4 from '../assets/priority/priority-p4.svg';

const API = import.meta.env.VITE_API_URL;
const LATEST_DATA_UPDATE = '18-August-2026';

const PRIORITY_ICONS = { 'P1': priorityP1, 'P2': priorityP2, 'P3': priorityP3, 'L': priorityP4 };
const PRIORITY_COLORS = { 'P1': '#e8f8f0', 'P2': '#f0fbf5', 'P3': '#fdf3e6', 'L': '#fdece6' };
const PRIORITY_TEXT_COLORS = { 'P1': '#16a34a', 'P2': '#16a34a', 'P3': '#d97706', 'L': '#dc2626' };

const TABS = [
  { table: 'fi_afzet',                  label: 'FI AFZET' },
  { table: 'fi_omzet',                  label: 'FI OMZET' },
  { table: 'fi_cu',                     label: 'FI CU' },
  { table: 'fi_ddd',                    label: 'FI DDD' },
  { table: 'retail_hosp',               label: 'Retail Hosp' },
  { table: 'z_index',                   label: 'Z-Index' },
  { table: 'taxe',                      label: 'Taxe' },
  { table: 'z_index_preferentiebeleid', label: 'Z-Index Preferentiebeleid' },
  { table: 'preferentiebeleid',         label: 'Preferentiebeleid' },
];

const NO_MOLECULE_TABS = new Set(['z_index_preferentiebeleid']);

const DEFAULT_PAGE_SIZE = 50;

const ACRONYMS = new Set([
  'ATC','KNMP','SGP','DDD','HPK','PRK','ZI','AIP','GVS','RVG',
  'MAH','UDH','WTG','GPK','TDV','CU','PPP','DOT','LIP','MAT',
  'EMEA','RVH',
]);

function humanizeColumn(col) {
  return col
    .split('_')
    .map(w => ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function fmtNum(v, dec = 2) {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return '';
  return n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function fmtPct(v) {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return '';
  return n.toFixed(2).replace('.', ',') + '%';
}

async function getToken(instance) {
  try {
    const accounts = instance.getAllAccounts();
    if (!accounts.length) return null;
    const resp = await instance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
      account: accounts[0],
    });
    return resp.idToken;
  } catch {
    const accounts = instance.getAllAccounts();
    return accounts[0]?.idToken || null;
  }
}

/* ═══════════════════════════════════════════════════════════
   Analysis Panel — renders the molecule analysis views
   ═══════════════════════════════════════════════════════════ */

function AnalysisPanel({ molecule, headers }) {
  const [view, setView] = useState('summary');
  const [descriptions, setDescriptions] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [zindexData, setZindexData] = useState(null);
  const [retailHospData, setRetailHospData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchView = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === 'summary') {
        const res = await fetch(`${API}/api/market-intelligence/molecule/summary?molecule=${encodeURIComponent(molecule)}`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setSummaryData(await res.json());
      } else if (view === 'molecule' || view.startsWith('desc_')) {
        const desc = view.startsWith('desc_') ? decodeURIComponent(view.slice(5)) : '';
        const url = `${API}/api/market-intelligence/molecule/detail?molecule=${encodeURIComponent(molecule)}` + (desc ? `&description=${encodeURIComponent(desc)}` : '');
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setDetailData(await res.json());
      } else if (view === 'zindex') {
        const res = await fetch(`${API}/api/market-intelligence/molecule/zindex?molecule=${encodeURIComponent(molecule)}`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setZindexData(await res.json());
      } else if (view === 'retail_hosp') {
        const res = await fetch(`${API}/api/market-intelligence/molecule/retail-hosp?molecule=${encodeURIComponent(molecule)}`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setRetailHospData(await res.json());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [molecule, view, headers]);

  // Fetch descriptions on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/market-intelligence/molecule/descriptions?molecule=${encodeURIComponent(molecule)}`, { headers });
        if (res.ok) setDescriptions(await res.json());
      } catch { /* empty */ }
    })();
    setView('summary');
  }, [molecule, headers]);

  useEffect(() => { fetchView(); }, [fetchView]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/market-intelligence/molecule/export?molecule=${encodeURIComponent(molecule)}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MACH_${molecule.replace(/[^a-zA-Z0-9_-]/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ marginBottom: 16, border: '1px solid var(--teal)', borderRadius: 10, background: 'var(--card)', overflow: 'hidden' }}>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #eef3f7', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, color: 'var(--t1)' }}>Analysis: {molecule}</strong>
        <select
          value={view}
          onChange={e => setView(e.target.value)}
          style={{ fontSize: 12.5, padding: '4px 8px', borderRadius: 5, border: '1px solid #d0d7de', background: 'var(--card)', color: 'var(--t1)', cursor: 'pointer' }}
        >
          <option value="summary">Summary</option>
          <option value="molecule">{molecule}</option>
          {descriptions.map(d => (
            <option key={d} value={'desc_' + encodeURIComponent(d)}>{d}</option>
          ))}
          <option value="zindex">Z-Index</option>
          <option value="retail_hosp">Retail Hosp</option>
        </select>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          style={{
            marginLeft: 'auto', padding: '5px 12px', fontSize: 12, fontWeight: 500,
            color: 'white', background: downloading ? '#999' : 'var(--teal)', border: 'none',
            borderRadius: 6, cursor: downloading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
          }}
        >{downloading ? 'Downloading...' : 'Download .xlsx'}</button>
      </div>

      {/* Content */}
      <div style={{ padding: 0 }}>
        {error && (
          <div style={{ padding: 16, color: '#991b1b', fontSize: 13 }}>Failed to load: {error}</div>
        )}
        {loading && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Loading...</div>
        )}

        {/* Summary view */}
        {!loading && !error && view === 'summary' && summaryData && (
          <SummaryView data={summaryData} headers={headers} />
        )}

        {/* Detail / description views */}
        {!loading && !error && (view === 'molecule' || view.startsWith('desc_')) && detailData && (
          <DetailView data={detailData} />
        )}

        {/* Z-Index view */}
        {!loading && !error && view === 'zindex' && zindexData && (
          <RawTableView data={zindexData} />
        )}

        {/* Retail Hosp view */}
        {!loading && !error && view === 'retail_hosp' && retailHospData && (
          <RawTableView data={retailHospData} />
        )}
      </div>
    </div>
  );
}

function getPriorityKey(val) {
  if (!val || typeof val !== 'string') return null;
  const upper = val.toUpperCase();
  if (upper.startsWith('P1')) return 'P1';
  if (upper.startsWith('P2')) return 'P2';
  if (upper.startsWith('P3')) return 'P3';
  if (upper.startsWith('LOW')) return 'L';
  return null;
}

// Columns that should never be hidden (indices 0–6 = icon placeholder not in data, No, Molecule, INN, Pack Form, Strength, Dosage Form, Pack Size)
const NEVER_HIDE_INDICES = new Set([0,1,2,3,4,5,6]);

const SWITCH_COLORS = { red: '#dc2626', orange: '#f59e0b', green: '#16a34a' };

function SummaryView({ data, headers }) {
  const navigate = useNavigate();
  const { columns = [], rows = [] } = data;
  const [switchMap, setSwitchMap] = useState({});

  // Fetch switch status for all molecules in the summary
  useEffect(() => {
    if (!rows.length) return;
    const mols = [...new Set(rows.map(r => r[1]).filter(Boolean))];
    if (!mols.length) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/market-intelligence/switch-status?molecules=${encodeURIComponent(mols.join(','))}`, { headers: headers || {} });
        if (res.ok) setSwitchMap(await res.json());
      } catch { /* ignore */ }
    })();
  }, [rows, headers]);

  if (!rows.length) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>No summary data</div>;

  // Text (left-aligned) columns by index: 0(No),1(Molecule),2(INN),3(Form),5(Dosage),7(PSF Name),10(Names),12(Priority),14(Retail<75%)
  const textCols = new Set([0,1,2,3,5,7,10,12,14]);
  // Priority is at column index 12

  // Compute which columns to hide (empty across all rows) — never hide first 7
  const { visibleIndices, hiddenLabels } = useMemo(() => {
    const hidden = [];
    const visible = [];
    for (let ci = 0; ci < columns.length; ci++) {
      if (NEVER_HIDE_INDICES.has(ci)) { visible.push(ci); continue; }
      const hasData = rows.some(row => {
        const v = row[ci];
        return v != null && v !== '' && v !== 0;
      });
      if (hasData) { visible.push(ci); } else { hidden.push(columns[ci]); }
    }
    return { visibleIndices: visible, hiddenLabels: hidden };
  }, [columns, rows]);

  return (
    <div>
      <style>{`
        @keyframes switchBlink { 0%,100%{opacity:1} 50%{opacity:.35} }
        .switch-indicator { animation: switchBlink 1.5s ease-in-out infinite; }
      `}</style>
      <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '50vh' }}>
        <table className="sup-table" style={{ tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th style={{ width: 28, padding: '4px 6px' }}></th>
              {visibleIndices.map(ci => <th key={ci}>{columns[ci]}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const pKey = getPriorityKey(row[12]);
              const icon = pKey ? PRIORITY_ICONS[pKey] : null;
              const bgColor = pKey ? PRIORITY_COLORS[pKey] : undefined;
              const mol = row[1] || '';
              const sw = switchMap[mol] || 'green';
              const swColor = SWITCH_COLORS[sw];
              const swClickable = sw === 'red' || sw === 'orange';
              return (
                <tr key={ri} style={bgColor ? { background: bgColor } : undefined}>
                  <td style={{ padding: '2px 6px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                      {icon && <img src={icon} alt={pKey} width={18} height={18} style={{ display: 'block' }} />}
                      {pKey && <span style={{ fontSize: 11, fontWeight: 600, color: PRIORITY_TEXT_COLORS[pKey] || 'var(--t2)', lineHeight: 1 }}>{pKey}</span>}
                      <span
                        className="switch-indicator"
                        title={sw === 'red' ? 'Red List' : sw === 'orange' ? 'Orange List' : 'Green (no switch concern)'}
                        onClick={swClickable ? () => navigate(`/market-intelligence/${sw === 'red' ? 'red' : 'orange'}-list`) : undefined}
                        style={{
                          display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                          background: swColor, cursor: swClickable ? 'pointer' : 'default',
                          flexShrink: 0,
                        }}
                      />
                    </div>
                  </td>
                  {visibleIndices.map(ci => (
                    <td key={ci} style={{ whiteSpace: 'nowrap', textAlign: textCols.has(ci) ? 'left' : 'right' }}>
                      {ci === 0 ? ri + 1
                        : row[ci] == null ? ''
                        : typeof row[ci] === 'number' && !textCols.has(ci) ? fmtNum(row[ci])
                        : row[ci]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {hiddenLabels.length > 0 && (
        <div style={{ padding: '6px 14px', fontSize: 11.5, color: 'var(--t3)', borderTop: '1px solid #eef3f7' }}>
          Hidden (no data): {hiddenLabels.join(', ')}
        </div>
      )}
    </div>
  );
}

function DetailView({ data }) {
  const { groups, labels = {} } = data;
  if (!groups || groups.length === 0) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>No detail data</div>;

  const hdrs = [
    'ATC Code', 'PRK Number', 'Name', 'Description', 'Strength', 'Form', 'CU per pack',
    '%Hosp/Retail',
    labels.fiOmzetPrev || 'Value MAT (prev)', labels.fiOmzetLast || 'Value MAT (last)',
    labels.fiCuPrev || 'CU MAT (prev)', labels.fiCuLast || 'CU MAT (last)',
    labels.fiAfzetPrev || 'Packs MAT (prev)', labels.fiAfzetLast || 'Packs MAT (last)',
    '% +/-', 'Lowest List Price', 'Lowest Company', 'Highest List Price', 'Highest Company',
    'Outlet', 'Grootste Volume', 'Volume MS%', 'All Competitors',
  ];

  const allRows = [];
  for (const grp of groups) {
    for (const r of grp) allRows.push(r);
    allRows.push(null); // spacer
  }

  return (
    <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '50vh' }}>
      <table className="sup-table" style={{ tableLayout: 'auto' }}>
        <thead>
          <tr>{hdrs.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {allRows.map((r, i) => {
            if (r === null) return <tr key={'sp' + i}><td colSpan={23} style={{ height: 8, background: 'transparent', border: 'none' }}></td></tr>;
            const isTotal = r.rowType === 'total';
            const style = isTotal ? { fontWeight: 700, color: '#dc2626' } : {};
            return (
              <tr key={i} style={style}>
                <td style={{ whiteSpace: 'nowrap' }}>{r.ATC}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.PRK_nr}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.Name}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.Description}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{r.Strength}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.Form}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{r.CUPack}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{r.HospRetail}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.ValPrev)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.ValLast)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.CuPrev)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.CuLast)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.PkPrev)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.PkLast)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtPct(r.PctDiff)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.LowestPrice)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.LowestCompany}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtNum(r.HighestPrice)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.HighestCompany}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.Outlet}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.GrootsteVolume}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtPct(r.VolumeMs)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.AllCompetitors}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RawTableView({ data }) {
  const { columns = [], rows = [] } = data;
  if (!columns.length) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>No data</div>;

  return (
    <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '50vh' }}>
      <table className="sup-table" style={{ tableLayout: 'auto' }}>
        <thead>
          <tr>{columns.map(c => <th key={c}>{humanizeColumn(c)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>No rows found</td></tr>
          ) : rows.map((row, i) => (
            <tr key={i}>
              {columns.map(c => (
                <td key={c} style={{ whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row[c] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main MACH Data page component
   ═══════════════════════════════════════════════════════════ */

export default function MachData() {
  const { instance } = useMsal();
  const [searchParams] = useSearchParams();
  const urlMolecule = searchParams.get('molecule') || '';
  const [activeTab, setActiveTab] = useState(TABS[0].table);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ columns: [], rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [molecule, setMolecule] = useState(urlMolecule);
  const [searchInput, setSearchInput] = useState(urlMolecule);
  const [authHeaders, setAuthHeaders] = useState({});

  // Keep auth headers fresh
  useEffect(() => {
    (async () => {
      const token = await getToken(instance);
      setAuthHeaders(token ? { Authorization: `Bearer ${token}` } : {});
    })();
  }, [instance]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({ page, pageSize: DEFAULT_PAGE_SIZE });
      if (molecule && !NO_MOLECULE_TABS.has(activeTab)) {
        params.set('molecule', molecule);
      }
      const res = await fetch(
        `${API}/api/market-intelligence/mach/${activeTab}?${params}`,
        { headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({ columns: json.columns || [], rows: json.rows || [], total: json.total || 0 });
    } catch (err) {
      setError(err.message);
      setData({ columns: [], rows: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [instance, activeTab, page, molecule]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTabChange = (table) => {
    if (table === activeTab) return;
    setActiveTab(table);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const val = searchInput.trim();
    if (val !== molecule) {
      setMolecule(val);
      setPage(1);
    }
  };

  const handleClear = () => {
    setSearchInput('');
    if (molecule) {
      setMolecule('');
      setPage(1);
    }
  };

  const searchDisabled = NO_MOLECULE_TABS.has(activeTab);

  const pageCount = Math.max(1, Math.ceil(data.total / DEFAULT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * DEFAULT_PAGE_SIZE;
  const pageEnd = Math.min(safePage * DEFAULT_PAGE_SIZE, data.total);

  const paginationNums = (() => {
    const win = 5;
    let s = Math.max(1, safePage - Math.floor(win / 2));
    let e = Math.min(pageCount, s + win - 1);
    s = Math.max(1, e - win + 1);
    const nums = [];
    for (let i = s; i <= e; i++) nums.push(i);
    return nums;
  })();

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 22px', background: 'var(--surf)' }}>
      <h4 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--t1)' }}>MACH Data</h4>
      <div style={{ marginBottom: 14, fontSize: 11.5, color: 'var(--t3)', display: 'inline-block', background: '#eef3f7', padding: '2px 10px', borderRadius: 4 }}>
        Latest Data Update: {LATEST_DATA_UPDATE}
      </div>

      {/* Search box */}
      <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, maxWidth: 420 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            type="text"
            placeholder={searchDisabled ? 'No molecule column' : 'Search molecule (exact match)'}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            disabled={searchDisabled}
            style={{
              width: '100%',
              padding: '7px 30px 7px 10px',
              fontSize: 13,
              border: '1px solid ' + (searchDisabled ? '#dee2e6' : 'var(--teal)'),
              borderRadius: 6,
              background: searchDisabled ? '#f5f5f5' : 'var(--card)',
              color: searchDisabled ? 'var(--t3)' : 'var(--t1)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {searchInput && !searchDisabled && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)',
                fontSize: 15, lineHeight: 1, padding: 0,
              }}
              title="Clear"
            >×</button>
          )}
        </div>
        <button
          type="submit"
          disabled={searchDisabled}
          style={{
            padding: '7px 14px',
            fontSize: 13,
            fontWeight: 500,
            color: searchDisabled ? 'var(--t3)' : 'white',
            background: searchDisabled ? '#dee2e6' : 'var(--teal)',
            border: 'none',
            borderRadius: 6,
            cursor: searchDisabled ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >Search</button>
      </form>

      {/* Analysis panel — shown when molecule is searched */}
      {molecule && (
        <AnalysisPanel molecule={molecule} headers={authHeaders} />
      )}

      {/* Excel-style tab bar */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0,
        marginBottom: 0,
        borderBottom: '2px solid var(--teal)',
        paddingLeft: 2,
      }}>
        {TABS.map(t => {
          const active = activeTab === t.table;
          return (
            <button
              key={t.table}
              type="button"
              onClick={() => handleTabChange(t.table)}
              style={{
                padding: '5px 12px',
                fontSize: 11.5,
                fontWeight: active ? 600 : 400,
                color: active ? 'var(--teal)' : 'var(--t2)',
                background: active ? 'var(--card)' : '#eef3f7',
                border: active
                  ? '2px solid var(--teal)'
                  : '1px solid #d0d7de',
                borderBottom: active ? '2px solid var(--card)' : 'none',
                borderRadius: '5px 5px 0 0',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                marginBottom: active ? -2 : 0,
                marginRight: 1,
                position: 'relative',
                zIndex: active ? 2 : 1,
                lineHeight: '18px',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginTop: 16 }}>
          Failed to load data: {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
          Loading...
        </div>
      )}

      {/* Table + Pagination */}
      {!loading && !error && (
        <div className="sup-table-wrap" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
          <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '72vh', WebkitOverflowScrolling: 'touch' }}>
            <table className="sup-table" style={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  {data.columns.map(col => (
                    <th key={col}>{humanizeColumn(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={data.columns.length || 1}
                      style={{ padding: 32, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}
                    >
                      No data available
                    </td>
                  </tr>
                ) : (
                  data.rows.map((row, i) => (
                    <tr key={i}>
                      {data.columns.map(col => (
                        <td key={col} style={{ whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row[col] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
            <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
              {paginationNums.map(n => (
                <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
              ))}
              <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
            </nav>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
              Showing {data.total === 0 ? 0 : pageStart + 1}–{pageEnd} of {data.total.toLocaleString('en-US')} rows
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
